import { Bag, BagResponseV4, updateProduct } from "@repo/apis/bag";
import { ExpandedBag } from "../utils/bag.type";
import { cache } from "../utils/cache";
import { buildBagExpansionsForApiInteraction } from "../utils/expansions";
import { handleApiInteractionError } from "../utils/handle-api-interaction-error";
import { handleApiInteractionSuccess } from "../utils/handle-api-interaction-success";
import { BagSdkMethod } from "../utils/method-initialiser";

type SharedUpdateProductParams = {
  variantId: number;
  quantity: number;
  bagItemId: string;
};

type Response<T> = {
  bag: T;
  messages: BagResponseV4["messages"];
};

type PublicUpdateProductMethod = {
  (
    params: SharedUpdateProductParams & { expand: true },
  ): Promise<Response<ExpandedBag>>;
  (
    params: SharedUpdateProductParams & { expand?: false },
  ): Promise<Response<Bag>>;
};

type UpdateProductMethod = BagSdkMethod<PublicUpdateProductMethod>;

export const updateProductInitialiser: UpdateProductMethod = (config) =>
  (async ({ expand, variantId, quantity, bagItemId }) => {
    const { chameleonRequestInstance, store, lang, getAuthHeader } = config;

    const authHeader = await getAuthHeader();
    const bagSummary = await cache.getSummary(config);
    const bagId = bagSummary?.bagId;

    if (!bagId) {
      throw new Error("Failed to update product. The bag does not exist");
    }

    const bagSessionToken = bagSummary?.sessionToken;

    if (!bagSessionToken) {
      throw new Error(
        "Failed to update product. The bag session token does not exist",
      );
    }

    const bagExpansions = buildBagExpansionsForApiInteraction({
      expand,
    });

    try {
      const updateProductResult = await updateProduct({
        chameleonRequestInstance,
        variantId,
        quantity,
        bagItemId,
        store,
        lang,
        authHeader,
        asosBagSessionToken: bagSessionToken as string,
        bagId,
        expand: bagExpansions,
      });

      await handleApiInteractionSuccess(config, {
        bag: updateProductResult.data.bag,
        sessionToken: updateProductResult.bagSessionToken,
      });

      return {
        bag: updateProductResult.data.bag,
        messages: updateProductResult.data.messages,
      };
    } catch (error: unknown) {
      return await handleApiInteractionError(config, error);
    }
  }) as PublicUpdateProductMethod;
